import { useEffect, useState } from 'react';
import { Row, Col, Spin, Button } from 'antd';
import { getOneState, getReport, getRoles, getStatesTree } from '../../../services/adminApi';
import { getListTreeForReports } from '../../../services/categoryApi';
import type { CategoryTreeNode } from '../monthly-test/types';
import { useAuth } from '../../../hooks/auth';
import BranchFilter from './components/BranchFilter';
import PeriodFilter from './components/PeriodFilter';
import TestsFilter from './components/TestsFilter';
import RolesFilter from './components/RolesFilter';
import {
  buildReportFileName,
  downloadBlobXlsx,
} from './helpers/reportHelpers';
import type {
  EmployeeReportFilters,
  EmployeeReportRequestFilters,
  ReportsProps,
  StateTreeNode,
  ReportOneState,
} from './reports.types';

const ReportEmployees = (_: ReportsProps) => {
  const [states, setStates] = useState<StateTreeNode[]>([]);
  const [oneState, setOneState] = useState<ReportOneState | null>(null);
  const [categoriesWithMaterials, setCategoriesWithMaterials] = useState<CategoryTreeNode[]>([]);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [roles, setRoles] = useState<Array<{ id: number; name: string }>>([]);
  const [filters, setFilters] = useState<EmployeeReportFilters>({
    state: [],
    period: [],
    tests: [],
    includedRoles: [4, 5, 6],
  });

  const { user } = useAuth();
  const hasOneStateAccess = user.type === 'registered' && Object.keys(user.extraData.permissions).length > 0;

  useEffect(() => {
    setIsLoading(true);

    if (hasOneStateAccess) {
      getOneState(user.stateId).then(result => {
        setOneState(result);
        setStates([]);
      });
    } else {
      getStatesTree().then(result => {
        setStates(result);
      });
    }

    getListTreeForReports(true).then(result => {
      setCategoriesWithMaterials(result);
    });

    getRoles().then(result => {
      setRoles(result);
      setIsLoading(false);
    });
  }, [hasOneStateAccess, user.stateId]);

  const getAvailableRoles = () => {
    const userRole = user.role;

    const filteredRoles = roles.filter(role => !['Администратор сервиса', 'Отчёты', 'Продавец'].includes(role.name) && role.id !== 0);

    const roleAccessMap: Record<number, number[]> = {
      4: [4, 5, 6],
      3: [3, 4, 5, 6],
      2: filteredRoles.map(r => r.id),
    };

    const allowedRoleIds = roleAccessMap[userRole] || filteredRoles.map(r => r.id);
    return filteredRoles.filter(role => allowedRoleIds.includes(role.id));
  };

  const availableRoles = getAvailableRoles();
  const defaultRoles = [
    { id: 4, name: 'Директор филиала' },
    { id: 5, name: 'Менеджер' },
    { id: 6, name: 'Руководитель отдела продаж' },
  ];
  const displayRoles = roles.length > 0 ? availableRoles : defaultRoles;

  const isValidReportPeriod = (selectedPeriod: string[]) => selectedPeriod.length === 2 && selectedPeriod.every((date) => Boolean(date));
  const isDownloadDisabled = !(hasOneStateAccess || filters.state.length > 0) || !isValidReportPeriod(filters.period) || filters.includedRoles.length === 0;

  const downloadReport = async () => {
    setIsLoadingReport(true);
    try {
      const reportFilters: EmployeeReportRequestFilters = {
        state: hasOneStateAccess ? [user.stateId] : filters.state,
        period: filters.period,
        content: [],
        tests: filters.tests.length > 0 ? filters.tests : undefined,
        includedRoles: filters.includedRoles.length > 0 ? filters.includedRoles : undefined,
      };
      const result = await getReport(reportFilters);
      downloadBlobXlsx(result, buildReportFileName(filters.period));
    } catch (error) {
      console.error('Ошибка при скачивании отчета:', error);
    } finally {
      setIsLoadingReport(false);
    }
  };

  return (
    <Spin spinning={isLoading}>
      <Row gutter={[16, 16]} style={{ marginTop: 40 }}>
        <Col span={24} style={{ fontSize: 25 }}>
          Отчеты
        </Col>
        <Col sm={8} xs={24}>
          <BranchFilter
            hasOneStateAccess={hasOneStateAccess}
            oneState={oneState}
            states={states}
            selectedState={filters.state}
            onChange={(values) => setFilters((prev) => ({ ...prev, state: values }))}
          />
        </Col>
        <Col sm={8} xs={24}>
          <TestsFilter
            label="Тесты"
            placeholder="Выберите тесты"
            categoriesWithMaterials={categoriesWithMaterials}
            selectedValues={filters.tests}
            onChange={(values) => setFilters((prev) => ({ ...prev, tests: values }))}
          />
        </Col>
        <Col sm={8} xs={24}>
          <PeriodFilter onChange={(dateString) => setFilters((prev) => ({ ...prev, period: dateString }))} />
        </Col>
        <Col span={24}>
          <RolesFilter
            roles={displayRoles}
            selectedRoles={filters.includedRoles}
            onChange={(values) => setFilters((prev) => ({ ...prev, includedRoles: values }))}
          />
        </Col>
        <Col>
          <Button onClick={downloadReport} disabled={isDownloadDisabled} loading={isLoadingReport && !isLoading}>
            {isLoadingReport && !isLoading ? 'Скачивание...' : 'Скачать'}
          </Button>
        </Col>
      </Row>
    </Spin>
  );
};

export default ReportEmployees;